import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Icon } from "semantic-ui-react";
import { useLoginCheck } from "../../hooks/useLoginCheck";
import "../../styles/scss/removeCustomerList.scss";

const COLUMNS = [
  { key: "created_at", title: "退会日", sortable: true },
  { key: "customer_id", title: "ID", sortable: true },
  { key: "first_name_yomi", title: "名前", sortable: true },
  { key: "studio", title: "店舗", sortable: true },
  { key: "start_month", title: "開始月", sortable: true },
  { key: "reason", title: "理由", sortable: true },
  { key: "reason_detail", title: "詳細", sortable: false },
];

function RemoveCustomerListPage() {
  useLoginCheck();
  const [removeCustomers, setRemoveCustomers] = useState([]);
  const [sortColumn, setSortColumn] = useState(null); // null = デフォルト (最新順)
  const [sortType, setSortType] = useState("desc");
  const [searchKeyword, setSearchKeyword] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState("list"); // 'list' | 'by-studio'

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/get_remove_customers/`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sort_column: sortColumn,
          sort_type: sortType,
        }),
      });
      if (!response.ok) throw new Error("network");
      const data = await response.json();
      setRemoveCustomers(data);
    } catch (e) {
      console.error("退会者の取得に失敗:", e);
      setError("データの取得中にエラーが発生しました。");
    } finally {
      setLoading(false);
    }
  }, [sortColumn, sortType]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortType((t) => (t === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortType("desc");
    }
  };

  // 今月の年月 (例: "2026-05")
  const thisMonth = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  }, []);

  const filtered = useMemo(() => {
    const k = searchKeyword.toLowerCase().trim();
    if (!k) return removeCustomers;
    return removeCustomers.filter((c) =>
      [
        c.customer_id?.toString(),
        c.first_name,
        c.last_name,
        c.first_name_yomi,
        c.last_name_yomi,
        c.studio,
        c.start_month,
        c.reason,
        c.reason_detail,
        c.created_at,
      ]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(k))
    );
  }, [removeCustomers, searchKeyword]);

  // サマリー
  const stats = useMemo(() => {
    const total = removeCustomers.length;
    const thisMonthCount = removeCustomers.filter((c) =>
      c.created_at?.startsWith(thisMonth)
    ).length;
    const reasons = new Set(removeCustomers.map((c) => c.reason).filter(Boolean));
    return { total, thisMonthCount, reasonKinds: reasons.size };
  }, [removeCustomers, thisMonth]);

  // 店舗別の理由集計 (検索結果ベース)
  const studioGroups = useMemo(() => {
    const map = new Map(); // studio -> { total, reasons: Map<reason, {count, details, customers}> }
    filtered.forEach((c) => {
      const studio = c.studio || "（未設定）";
      const reason = c.reason || "（未記入）";
      if (!map.has(studio)) {
        map.set(studio, { total: 0, reasons: new Map() });
      }
      const entry = map.get(studio);
      entry.total += 1;
      if (!entry.reasons.has(reason)) {
        entry.reasons.set(reason, { count: 0, items: [] });
      }
      const r = entry.reasons.get(reason);
      r.count += 1;
      r.items.push({
        date: c.created_at,
        name:
          `${c.first_name || ""} ${c.last_name || ""}`.trim() ||
          `#${c.customer_id}`,
        detail: (c.reason_detail || "").trim(),
      });
    });
    return Array.from(map.entries())
      .map(([studio, { total, reasons }]) => ({
        studio,
        total,
        reasons: Array.from(reasons.entries())
          .map(([name, { count, items }]) => ({ name, count, items }))
          .sort((a, b) => b.count - a.count),
      }))
      .sort((a, b) => b.total - a.total);
  }, [filtered]);

  const [expandedKeys, setExpandedKeys] = useState(new Set());

  const toggleExpand = (key) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const isOtherReason = (name) => /その他|その他理由|other/i.test(name || "");

  const formatYmd = (ymd) => {
    if (!ymd) return { y: "—", md: "—" };
    const [y, m, d] = ymd.split("-");
    return { y, md: `${m}/${d}` };
  };

  return (
    <div className="remove-customer-list-page">
      {/* header */}
      <div className="page-header">
        <div>
          <p className="eyebrow">Cancellations</p>
          <div className="title-row">
            <h1>退会者一覧</h1>
            <span className="count-pill">
              <span className="num">{filtered.length}</span>
              <span>名</span>
            </span>
          </div>
        </div>
        <div className="search-bar">
          <Icon name="search" />
          <input
            type="text"
            placeholder="名前 / 店舗 / 理由 等で検索"
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
          />
        </div>
      </div>

      {/* summary + view toggle */}
      <div className="summary-row">
        <span className="summary-chip">
          Total <span className="num">{stats.total}</span>
        </span>
        <span className="summary-chip this-month">
          This Month <span className="num">{stats.thisMonthCount}</span>
        </span>
        <span className="summary-chip">
          Reasons <span className="num">{stats.reasonKinds}</span>
        </span>
        <div style={{ flex: 1 }} />
        <div className="view-toggle">
          <button
            className={viewMode === "list" ? "active" : ""}
            onClick={() => setViewMode("list")}
          >
            <span className="ja">一覧</span>
          </button>
          <button
            className={viewMode === "by-studio" ? "active" : ""}
            onClick={() => setViewMode("by-studio")}
          >
            <span className="ja">店舗別</span>
          </button>
        </div>
      </div>

      {/* by-studio view */}
      {viewMode === "by-studio" && (
        <div className="studio-grid">
          {loading && removeCustomers.length === 0 ? (
            Array.from({ length: 6 }).map((_, i) => (
              <div className="studio-card" key={i}>
                <div className="sc-header">
                  <div className="sc-title">
                    <span className="eyebrow-en">Loading</span>
                    <span className="ja">—</span>
                  </div>
                </div>
                <div className="sc-body">
                  {Array.from({ length: 3 }).map((_, j) => (
                    <div className="reason-row" key={j}>
                      <div className="r-bar">
                        <span className="fill" style={{ width: "50%" }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          ) : studioGroups.length === 0 ? (
            <div className="empty-state" style={{ gridColumn: "1 / -1" }}>
              <p className="ja">該当する退会者がいません</p>
              <p className="en">No matching records</p>
            </div>
          ) : (
            studioGroups.map((g) => {
              const max = g.reasons[0]?.count || 1;
              return (
                <div className="studio-card" key={g.studio}>
                  <div className="sc-header">
                    <div className="sc-title">
                      <span className="eyebrow-en">Studio</span>
                      <span className="ja" title={g.studio}>
                        {g.studio}
                      </span>
                    </div>
                    <div className="sc-total">
                      <span className="num">{g.total}</span>
                      <span className="lab">退会</span>
                    </div>
                  </div>
                  <div className="sc-body">
                    {g.reasons.length === 0 ? (
                      <div className="sc-empty">記録なし</div>
                    ) : (
                      g.reasons.map((r) => {
                        const pct = (r.count / g.total) * 100;
                        const w = (r.count / max) * 100;
                        const key = `${g.studio}|${r.name}`;
                        const detailItems = r.items.filter(
                          (it) => it.detail
                        );
                        const isOther = isOtherReason(r.name);
                        const canExpand = detailItems.length > 0;
                        const expanded = canExpand && expandedKeys.has(key);
                        const handleToggle = () => {
                          if (canExpand) toggleExpand(key);
                        };
                        return (
                          <div
                            className={`reason-row ${
                              isOther ? "is-other" : ""
                            } ${expanded ? "is-expanded" : ""}`}
                            key={r.name}
                          >
                            <div
                              className="r-head"
                              onClick={canExpand ? handleToggle : undefined}
                              style={{
                                cursor: canExpand ? "pointer" : "default",
                              }}
                            >
                              <span className="r-name" title={r.name}>
                                {r.name}
                                {canExpand && (
                                  <span className="r-chev">
                                    {expanded ? "▾" : "▸"}
                                  </span>
                                )}
                              </span>
                              <span className="r-meta">
                                <span className="r-pct">
                                  {pct.toFixed(0)}%
                                </span>
                                <span className="r-num">{r.count}</span>
                              </span>
                            </div>
                            <div className="r-bar">
                              <span
                                className="fill"
                                style={{ width: `${w}%` }}
                              />
                            </div>
                            {canExpand && expanded && (
                              <ul className="r-details">
                                {detailItems.map((it, i) => (
                                  <li key={i}>
                                    <span className="d-date">
                                      {`${(it.date || "").replace(/-/g, ".")}  ${
                                        it.name
                                      }`}
                                    </span>
                                    <span className="d-text">{it.detail}</span>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* panel */}
      {viewMode === "list" && (
      <div className="panel">
        <div className="panel-header">
          <p className="section-title">
            <span className="ja">退会一覧</span>
          </p>
          <span className="panel-meta">
            {sortColumn ? `${sortColumn} / ${sortType}` : "最新順"}
          </span>
        </div>
        <div className="panel-body">
          <div className="rc-grid">
            <div className="grid-row grid-head">
              {COLUMNS.map((col) => (
                <div
                  className="cell"
                  key={col.key}
                  onClick={() => col.sortable && handleSort(col.key)}
                  style={{ cursor: col.sortable ? "pointer" : "default" }}
                >
                  {col.title}
                  {col.sortable && sortColumn === col.key && (
                    <span className="sort-ind">
                      {sortType === "asc" ? "▲" : "▼"}
                    </span>
                  )}
                </div>
              ))}
            </div>

            {loading && removeCustomers.length === 0 ? (
              Array.from({ length: 8 }).map((_, i) => (
                <div className="skeleton-row" key={i}>
                  {Array.from({ length: 7 }).map((_, j) => (
                    <span className="bar" key={j} />
                  ))}
                </div>
              ))
            ) : error ? (
              <div className="error-block">
                <p className="em">{error}</p>
                <button onClick={fetchData}>再読み込み</button>
              </div>
            ) : filtered.length === 0 ? (
              <div className="empty-state">
                <p className="ja">該当する退会者がいません</p>
                <p className="en">No matching records</p>
              </div>
            ) : (
              filtered.map((c, i) => {
                const ymd = formatYmd(c.created_at);
                const isRecent = c.created_at?.startsWith(thisMonth);
                return (
                  <div
                    className={`grid-row ${isRecent ? "is-recent" : ""}`}
                    key={`${c.customer_id}-${i}`}
                  >
                    <div className="cell date">
                      <span className="ymd">
                        <span className="y">{ymd.y}</span>
                        <span className="md">{ymd.md}</span>
                      </span>
                    </div>
                    <div className="cell id">#{c.customer_id}</div>
                    <div className="cell name">
                      <span className="ja">
                        {`${c.first_name || ""} ${c.last_name || ""}`.trim() ||
                          "—"}
                      </span>
                      {(c.first_name_yomi || c.last_name_yomi) && (
                        <span className="yomi">
                          {`${c.first_name_yomi || ""} ${
                            c.last_name_yomi || ""
                          }`.trim()}
                        </span>
                      )}
                    </div>
                    <div className="cell studio">
                      <span className="studio-chip">{c.studio || "—"}</span>
                    </div>
                    <div className="cell month">{c.start_month || "—"}</div>
                    <div className="cell reason">
                      {c.reason ? (
                        <span className="reason-chip">{c.reason}</span>
                      ) : (
                        "—"
                      )}
                    </div>
                    <div className="cell detail" title={c.reason_detail}>
                      {c.reason_detail || "—"}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
      )}
    </div>
  );
}

export default RemoveCustomerListPage;
